// InputDataCompanyModal.tsx
import {
  ActionIcon,
  Button,
  Flex,
  Loader,
  Modal,
  MultiSelect,
  ScrollArea,
  Select,
  Tabs,
  Text,
} from "@mantine/core";
import { Dropzone, FileWithPath } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";

import { useCompanyOptions } from "../../../api/hooks/customer/useCompanyOptions";
import { useCreateFileRecommendation } from "../../../api/hooks/fileRecommendation/useCreateFileRecommendation";
import { useFileRecommendationsByFeature } from "../../../api/hooks/fileRecommendation/useFileRecommendationsByFeature";
import { useUploadFiles } from "../../../api/hooks/fileStorage/useUploadFiles";
import { getCompanyOptions } from "../../../utils/company";
import {
  useKTPDocuments,
  useNPWPCompanyDocuments,
  useNPWPIndividuDocuments,
} from "../hooks/useIDPDocumentsForKYC";
import { CompanyKYCFormData } from "../types";

interface InputDataCompanyModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (data: CompanyKYCFormData) => void;
}

interface UploadedFile {
  file: FileWithPath;
  url: string;
}

interface DocumentUploadWithRecommendationProps {
  label: string;
  uploadedFile: UploadedFile | null;
  selectedRecommendation: string | null;
  isUploading: boolean;
  onDrop: (files: FileWithPath[]) => void;
  onSelectRecommendation: (url: string | null) => void;
  recommendations: { value: string; label: string }[];
  loadingRecommendations: boolean;
  required?: boolean;
  disabled?: boolean;
}

interface MultiDocumentUploadWithRecommendationProps {
  label: string;
  uploadedFiles: UploadedFile[];
  selectedRecommendations: string[];
  isUploading: boolean;
  onDrop: (files: FileWithPath[]) => void;
  onRemove: (index: number) => void;
  onSelectRecommendations: (urls: string[]) => void;
  onRemoveRecommendation: (url: string) => void;
  recommendations: { value: string; label: string }[];
  loadingRecommendations: boolean;
  required?: boolean;
  disabled?: boolean;
}

const DownloadIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 16L12 8"
      stroke="#1a2332"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M9 13L12 16L15 13"
      stroke="#1a2332"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 17V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V17"
      stroke="#1a2332"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" fill="#22c55e" />
    <path
      d="M8 12L11 15L16 9"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DocumentUploadWithRecommendation = ({
  label,
  uploadedFile,
  selectedRecommendation,
  isUploading,
  onDrop,
  onSelectRecommendation,
  recommendations,
  loadingRecommendations,
  required,
  disabled,
}: DocumentUploadWithRecommendationProps) => {
  const [activeTab, setActiveTab] = useState<string | null>("upload");

  const hasValue = uploadedFile || selectedRecommendation;

  return (
    <Flex direction="column" gap={8}>
      {label && (
        <Text size="sm" weight={600}>
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </Text>
      )}
      <Tabs value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="upload">Upload New</Tabs.Tab>
          <Tabs.Tab
            value="recommendation"
            disabled={recommendations.length === 0 && !loadingRecommendations}
          >
            Select Existing ({recommendations.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="upload" pt="xs">
          <Dropzone
            onDrop={(files) => {
              onSelectRecommendation(null);
              onDrop(files);
            }}
            accept={["application/pdf", "image/*"]}
            maxSize={10 * 1024 ** 25}
            disabled={isUploading || disabled}
            styles={{
              root: {
                borderRadius: "24px",
                border: `2px solid ${
                  uploadedFile && !selectedRecommendation
                    ? "#22c55e"
                    : "#1a2332"
                }`,
                padding: "12px 16px",
                minHeight: "auto",
                backgroundColor:
                  uploadedFile && !selectedRecommendation ? "#f0fdf4" : "white",
                opacity: isUploading || disabled ? 0.7 : 1,
              },
            }}
          >
            <Flex align="center" gap={12}>
              {isUploading ? (
                <>
                  <Loader size="sm" />
                  <Text size="sm" color="dimmed" style={{ flex: 1 }}>
                    Uploading...
                  </Text>
                </>
              ) : uploadedFile && !selectedRecommendation ? (
                <>
                  <CheckIcon />
                  <Text size="sm" color="green" style={{ flex: 1 }}>
                    {uploadedFile.file.name}
                  </Text>
                </>
              ) : (
                <>
                  <Text size="sm" color="dimmed" style={{ flex: 1 }}>
                    Drop document here
                  </Text>
                  <DownloadIcon />
                </>
              )}
            </Flex>
          </Dropzone>
        </Tabs.Panel>

        <Tabs.Panel value="recommendation" pt="xs">
          {loadingRecommendations ? (
            <Flex align="center" gap={8} p={12}>
              <Loader size="sm" />
              <Text size="sm" color="dimmed">
                Loading recommendations...
              </Text>
            </Flex>
          ) : (
            <Select
              placeholder="Select from existing files"
              value={selectedRecommendation}
              onChange={onSelectRecommendation}
              data={recommendations}
              searchable
              clearable
              disabled={disabled}
              nothingFound="No recommendations found"
            />
          )}
          {selectedRecommendation && (
            <Flex
              align="center"
              gap={8}
              mt={8}
              style={{
                backgroundColor: "#f0fdf4",
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #22c55e",
              }}
            >
              <CheckIcon />
              <Text size="sm" color="green" style={{ flex: 1 }}>
                {recommendations.find((r) => r.value === selectedRecommendation)
                  ?.label || selectedRecommendation}
              </Text>
            </Flex>
          )}
        </Tabs.Panel>
      </Tabs>
      {hasValue && activeTab !== "recommendation" && uploadedFile && (
        <Text size="xs" color="dimmed">
          Selected: Uploaded file
        </Text>
      )}
      {hasValue && activeTab !== "upload" && selectedRecommendation && (
        <Text size="xs" color="dimmed">
          Selected: Existing file
        </Text>
      )}
    </Flex>
  );
};

const MultiDocumentUploadWithRecommendation = ({
  label,
  uploadedFiles,
  selectedRecommendations,
  isUploading,
  onDrop,
  onRemove,
  onSelectRecommendations,
  onRemoveRecommendation,
  recommendations,
  loadingRecommendations,
  required,
  disabled,
}: MultiDocumentUploadWithRecommendationProps) => {
  const [activeTab, setActiveTab] = useState<string | null>("upload");

  return (
    <Flex direction="column" gap={8}>
      {label && (
        <Text size="sm" weight={600}>
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </Text>
      )}
      <Tabs value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="upload">Upload New</Tabs.Tab>
          <Tabs.Tab
            value="recommendation"
            disabled={recommendations.length === 0 && !loadingRecommendations}
          >
            Select Existing ({recommendations.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="upload" pt="xs">
          <Dropzone
            onDrop={onDrop}
            accept={["application/pdf", "image/*"]}
            maxSize={10 * 1024 ** 2}
            disabled={isUploading || disabled}
            styles={{
              root: {
                borderRadius: "24px",
                border: `2px solid ${
                  uploadedFiles.length > 0 ? "#22c55e" : "#1a2332"
                }`,
                padding: "12px 16px",
                minHeight: "auto",
                backgroundColor: uploadedFiles.length > 0 ? "#f0fdf4" : "white",
                opacity: isUploading || disabled ? 0.7 : 1,
              },
            }}
          >
            <Flex align="center" gap={12}>
              {isUploading ? (
                <>
                  <Loader size="sm" />
                  <Text size="sm" color="dimmed" style={{ flex: 1 }}>
                    Uploading...
                  </Text>
                </>
              ) : (
                <>
                  <Text size="sm" color="dimmed" style={{ flex: 1 }}>
                    Drop documents here (multiple allowed)
                  </Text>
                  <DownloadIcon />
                </>
              )}
            </Flex>
          </Dropzone>
          {uploadedFiles.length > 0 && (
            <Flex direction="column" gap={4} mt={8}>
              {uploadedFiles.map((file, index) => (
                <Flex
                  key={index}
                  align="center"
                  gap={8}
                  style={{
                    backgroundColor: "#f0fdf4",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #22c55e",
                  }}
                >
                  <CheckIcon />
                  <Text size="sm" color="green" style={{ flex: 1 }}>
                    {file.file.name}
                  </Text>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    onClick={() => onRemove(index)}
                  >
                    <CloseIcon />
                  </ActionIcon>
                </Flex>
              ))}
            </Flex>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="recommendation" pt="xs">
          {loadingRecommendations ? (
            <Flex align="center" gap={8} p={12}>
              <Loader size="sm" />
              <Text size="sm" color="dimmed">
                Loading recommendations...
              </Text>
            </Flex>
          ) : (
            <MultiSelect
              placeholder="Select from existing files"
              value={selectedRecommendations}
              onChange={onSelectRecommendations}
              data={recommendations}
              searchable
              clearable
              disabled={disabled}
              nothingFound="No recommendations found"
            />
          )}
          {selectedRecommendations.length > 0 && (
            <Flex direction="column" gap={4} mt={8}>
              {selectedRecommendations.map((url) => (
                <Flex
                  key={url}
                  align="center"
                  gap={8}
                  style={{
                    backgroundColor: "#f0fdf4",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #22c55e",
                  }}
                >
                  <CheckIcon />
                  <Text size="sm" color="green" style={{ flex: 1 }}>
                    {recommendations.find((r) => r.value === url)?.label || url}
                  </Text>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    onClick={() => onRemoveRecommendation(url)}
                  >
                    <CloseIcon />
                  </ActionIcon>
                </Flex>
              ))}
            </Flex>
          )}
        </Tabs.Panel>
      </Tabs>
    </Flex>
  );
};

const InputDataCompanyModal = ({
  opened,
  onClose,
  onSubmit,
}: InputDataCompanyModalProps) => {
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [ktpIds, setKtpIds] = useState<string[]>([]);
  const [npwpCompanyId, setNpwpCompanyId] = useState<string | null>(null);
  const [npwpIndividuIds, setNpwpIndividuIds] = useState<string[]>([]);

  const [ahuFile, setAhuFile] = useState<UploadedFile | null>(null);
  const [nibFile, setNibFile] = useState<UploadedFile | null>(null);
  const [newestAktaFile, setNewestAktaFile] = useState<UploadedFile | null>(
    null
  );
  const [pendirianAktaFile, setPendirianAktaFile] =
    useState<UploadedFile | null>(null);
  const [passportFiles, setPassportFiles] = useState<UploadedFile[]>([]);

  const [ahuRecommendation, setAhuRecommendation] = useState<string | null>(
    null
  );
  const [nibRecommendation, setNibRecommendation] = useState<string | null>(
    null
  );
  const [newestAktaRecommendation, setNewestAktaRecommendation] = useState<
    string | null
  >(null);
  const [pendirianAktaRecommendation, setPendirianAktaRecommendation] =
    useState<string | null>(null);
  const [passportRecommendations, setPassportRecommendations] = useState<
    string[]
  >([]);

  const [isUploadingAhu, setIsUploadingAhu] = useState(false);
  const [isUploadingNib, setIsUploadingNib] = useState(false);
  const [isUploadingNewestAkta, setIsUploadingNewestAkta] = useState(false);
  const [isUploadingPendirianAkta, setIsUploadingPendirianAkta] =
    useState(false);
  const [isUploadingPassport, setIsUploadingPassport] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [, uploadFiles] = useUploadFiles();
  const [, createFileRecommendation] = useCreateFileRecommendation();

  const customerIdNumber = customerId ? Number(customerId) : undefined;

  const [{ data: ahuRecommendationsData, fetching: fetchingAhuRecs }] =
    useFileRecommendationsByFeature({
      feature: "kyc",
      inputField: "ahu",
      customerId: customerIdNumber,
    });

  const [{ data: nibRecommendationsData, fetching: fetchingNibRecs }] =
    useFileRecommendationsByFeature({
      feature: "kyc",
      inputField: "nib",
      customerId: customerIdNumber,
    });

  const [
    { data: newestAktaRecommendationsData, fetching: fetchingNewestAktaRecs },
  ] = useFileRecommendationsByFeature({
    feature: "kyc",
    inputField: "newest_akta",
    customerId: customerIdNumber,
  });

  const [
    {
      data: pendirianAktaRecommendationsData,
      fetching: fetchingPendirianAktaRecs,
    },
  ] = useFileRecommendationsByFeature({
    feature: "kyc",
    inputField: "pendirian_akta",
    customerId: customerIdNumber,
  });

  const [
    { data: passportRecommendationsData, fetching: fetchingPassportRecs },
  ] = useFileRecommendationsByFeature({
    feature: "kyc",
    inputField: "passport",
    customerId: customerIdNumber,
  });

  const ahuRecommendations = useMemo(
    () =>
      (ahuRecommendationsData?.fileRecommendationsByFeature || [])
        .slice(0, 5)
        .map((rec) => ({ value: rec.fileUrl, label: rec.fileName })),
    [ahuRecommendationsData]
  );

  const nibRecommendations = useMemo(
    () =>
      (nibRecommendationsData?.fileRecommendationsByFeature || [])
        .slice(0, 5)
        .map((rec) => ({ value: rec.fileUrl, label: rec.fileName })),
    [nibRecommendationsData]
  );

  const newestAktaRecommendations = useMemo(
    () =>
      (newestAktaRecommendationsData?.fileRecommendationsByFeature || [])
        .slice(0, 5)
        .map((rec) => ({ value: rec.fileUrl, label: rec.fileName })),
    [newestAktaRecommendationsData]
  );

  const pendirianAktaRecommendations = useMemo(
    () =>
      (pendirianAktaRecommendationsData?.fileRecommendationsByFeature || [])
        .slice(0, 5)
        .map((rec) => ({ value: rec.fileUrl, label: rec.fileName })),
    [pendirianAktaRecommendationsData]
  );

  const passportRecommendationOptions = useMemo(
    () =>
      (passportRecommendationsData?.fileRecommendationsByFeature || [])
        .slice(0, 5)
        .map((rec) => ({ value: rec.fileUrl, label: rec.fileName })),
    [passportRecommendationsData]
  );

  useEffect(() => {
    if (opened) {
      setCustomerId(null);
      setKtpIds([]);
      setNpwpCompanyId(null);
      setNpwpIndividuIds([]);
      setAhuFile(null);
      setNibFile(null);
      setNewestAktaFile(null);
      setPendirianAktaFile(null);
      setPassportFiles([]);
      setAhuRecommendation(null);
      setNibRecommendation(null);
      setNewestAktaRecommendation(null);
      setPendirianAktaRecommendation(null);
      setPassportRecommendations([]);
      setIsUploadingAhu(false);
      setIsUploadingNib(false);
      setIsUploadingNewestAkta(false);
      setIsUploadingPendirianAkta(false);
      setIsUploadingPassport(false);
    }
  }, [opened]);

  const [{ data: customersResponse, fetching: fetchingCustomers }] =
    useCompanyOptions();
  const customers = customersResponse?.customers.data;

  const { options: ktpOptions, loading: loadingKtp } = useKTPDocuments(
    customerIdNumber ?? null
  );
  const { options: npwpCompanyOptions, loading: loadingNpwpCompany } =
    useNPWPCompanyDocuments(customerIdNumber ?? null);
  const { options: npwpIndividuOptions, loading: loadingNpwpIndividu } =
    useNPWPIndividuDocuments(customerIdNumber ?? null);

  const ktpSelectData = useMemo(
    () =>
      ktpOptions.map((opt) => ({ value: String(opt.id), label: opt.label })),
    [ktpOptions]
  );

  const npwpCompanySelectData = useMemo(
    () =>
      npwpCompanyOptions.map((opt) => ({
        value: String(opt.id),
        label: opt.label,
      })),
    [npwpCompanyOptions]
  );

  const npwpIndividuSelectData = useMemo(
    () =>
      npwpIndividuOptions.map((opt) => ({
        value: String(opt.id),
        label: opt.label,
      })),
    [npwpIndividuOptions]
  );

  const handleFileUpload = async (
    file: FileWithPath,
    setUploadedFile: (file: UploadedFile | null) => void,
    setIsUploading: (loading: boolean) => void,
    inputField: string
  ) => {
    setIsUploading(true);

    try {
      const uploadResult = await uploadFiles({ input: [file] });

      if (uploadResult.error || !uploadResult.data?.uploadFiles?.[0]) {
        notifications.show({
          title: "Error",
          message: "Failed to upload file",
          color: "red",
        });
        setUploadedFile(null);
        return;
      }

      const uploadedUrl = uploadResult.data.uploadFiles[0].filePath;
      setUploadedFile({ file, url: uploadedUrl });

      await createFileRecommendation({
        input: {
          customerId: customerIdNumber,
          fileName: file.name,
          fileUrl: uploadedUrl,
          feature: "kyc",
          inputField: inputField,
        },
      });
    } catch (error) {
      console.error("Upload error:", error);
      notifications.show({
        title: "Error",
        message: "An error occurred while uploading",
        color: "red",
      });
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleMultiFileUpload = async (files: FileWithPath[]) => {
    setIsUploadingPassport(true);

    try {
      const uploadResult = await uploadFiles({ input: files });

      if (uploadResult.error || !uploadResult.data?.uploadFiles) {
        notifications.show({
          title: "Error",
          message: "Failed to upload files",
          color: "red",
        });
        return;
      }

      const newUploadedFiles: UploadedFile[] = files.map((file, index) => ({
        file,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        url: uploadResult.data!.uploadFiles[index].filePath,
      }));

      setPassportFiles((prev) => [...prev, ...newUploadedFiles]);

      await Promise.all(
        newUploadedFiles.map((uploadedFile) =>
          createFileRecommendation({
            input: {
              customerId: customerIdNumber,
              fileName: uploadedFile.file.name,
              fileUrl: uploadedFile.url,
              feature: "kyc",
              inputField: "passport",
            },
          })
        )
      );
    } catch (error) {
      console.error("Upload error:", error);
      notifications.show({
        title: "Error",
        message: "An error occurred while uploading",
        color: "red",
      });
    } finally {
      setIsUploadingPassport(false);
    }
  };

  const handleRemovePassport = (index: number) => {
    setPassportFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemovePassportRecommendation = (url: string) => {
    setPassportRecommendations((prev) => prev.filter((u) => u !== url));
  };

  const handleAhuDrop = (files: FileWithPath[]) => {
    if (files[0]) {
      setAhuRecommendation(null);
      handleFileUpload(files[0], setAhuFile, setIsUploadingAhu, "ahu");
    }
  };

  const handleNibDrop = (files: FileWithPath[]) => {
    if (files[0]) {
      setNibRecommendation(null);
      handleFileUpload(files[0], setNibFile, setIsUploadingNib, "nib");
    }
  };

  const handleNewestAktaDrop = (files: FileWithPath[]) => {
    if (files[0]) {
      setNewestAktaRecommendation(null);
      handleFileUpload(
        files[0],
        setNewestAktaFile,
        setIsUploadingNewestAkta,
        "newest_akta"
      );
    }
  };

  const handlePendirianAktaDrop = (files: FileWithPath[]) => {
    if (files[0]) {
      setPendirianAktaRecommendation(null);
      handleFileUpload(
        files[0],
        setPendirianAktaFile,
        setIsUploadingPendirianAkta,
        "pendirian_akta"
      );
    }
  };

  const handlePassportDrop = (files: FileWithPath[]) => {
    if (files.length > 0) {
      handleMultiFileUpload(files);
    }
  };

  const handleAhuRecommendationSelect = (url: string | null) => {
    setAhuRecommendation(url);
    if (url) setAhuFile(null);
  };

  const handleNibRecommendationSelect = (url: string | null) => {
    setNibRecommendation(url);
    if (url) setNibFile(null);
  };

  const handleNewestAktaRecommendationSelect = (url: string | null) => {
    setNewestAktaRecommendation(url);
    if (url) setNewestAktaFile(null);
  };

  const handlePendirianAktaRecommendationSelect = (url: string | null) => {
    setPendirianAktaRecommendation(url);
    if (url) setPendirianAktaFile(null);
  };

  const getAhuUrl = () => ahuRecommendation || ahuFile?.url || null;
  const getNibUrl = () => nibRecommendation || nibFile?.url || null;
  const getNewestAktaUrl = () =>
    newestAktaRecommendation || newestAktaFile?.url || null;
  const getPendirianAktaUrl = () =>
    pendirianAktaRecommendation || pendirianAktaFile?.url || null;
  const getPassportUrls = () => {
    const uploadedUrls = passportFiles.map((f) => f.url);
    return [...uploadedUrls, ...passportRecommendations];
  };

  const handleSubmit = () => {
    const nibUrl = getNibUrl();
    const newestAktaUrl = getNewestAktaUrl();

    if (
      !customerId ||
      ktpIds.length === 0 ||
      !npwpCompanyId ||
      !nibUrl ||
      !newestAktaUrl
    )
      return;

    setIsSubmitting(true);

    const passportUrls = getPassportUrls();

    onSubmit({
      customerId: Number(customerId),
      ktpIds: ktpIds.map(Number),
      npwpCompanyId: Number(npwpCompanyId),
      npwpIndividuIds:
        npwpIndividuIds.length > 0 ? npwpIndividuIds.map(Number) : undefined,
      ahuUrl: getAhuUrl() || undefined,
      nibUrl,
      newestAktaUrl,
      pendirianAktaUrl: getPendirianAktaUrl() || undefined,
      passportUrls: passportUrls.length > 0 ? passportUrls : undefined,
    });

    setIsSubmitting(false);
  };

  const isAnyUploading =
    isUploadingAhu ||
    isUploadingNib ||
    isUploadingNewestAkta ||
    isUploadingPendirianAkta ||
    isUploadingPassport;

  const isFormValid =
    customerId &&
    ktpIds.length > 0 &&
    npwpCompanyId &&
    (nibFile || nibRecommendation) &&
    (newestAktaFile || newestAktaRecommendation) &&
    !isAnyUploading;

  const handleCustomerChange = (value: string | null) => {
    setCustomerId(value);
    setKtpIds([]);
    setNpwpCompanyId(null);
    setNpwpIndividuIds([]);
    setAhuFile(null);
    setNibFile(null);
    setNewestAktaFile(null);
    setPendirianAktaFile(null);
    setPassportFiles([]);
    setAhuRecommendation(null);
    setNibRecommendation(null);
    setNewestAktaRecommendation(null);
    setPendirianAktaRecommendation(null);
    setPassportRecommendations([]);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="xl"
      title={
        <Text size="xl" weight={700}>
          Input Data - Company
        </Text>
      }
      closeOnClickOutside={!isAnyUploading && !isSubmitting}
      closeOnEscape={!isAnyUploading && !isSubmitting}
      styles={{
        body: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          height: "70vh",
        },
      }}
    >
      <ScrollArea style={{ flex: 1 }} offsetScrollbars>
        <Flex direction="column" gap={20} p={20}>
          <Select
            label="Company Name"
            placeholder="Select Company"
            value={customerId}
            onChange={handleCustomerChange}
            data={getCompanyOptions(customers)}
            searchable
            clearable
            size="md"
            disabled={fetchingCustomers || isAnyUploading || isSubmitting}
            required
            withAsterisk
            styles={{
              label: {
                fontSize: "16px",
                fontWeight: 600,
                marginBottom: "8px",
                color: "#1a2332",
              },
            }}
          />

          <div style={{ borderTop: "2px dashed #1a2332", margin: "10px 0" }} />

          <Text size="md" weight={600}>
            Select from IDP & FD Documents
          </Text>

          <Flex direction="row" gap={20} wrap="wrap">
            <div style={{ flex: "1 1 45%" }}>
              <MultiSelect
                label="KTP Pengurus"
                placeholder={
                  customerId ? "Select KTP documents" : "Select company first"
                }
                value={ktpIds}
                onChange={setKtpIds}
                data={ktpSelectData}
                searchable
                clearable
                size="md"
                disabled={
                  !customerId || loadingKtp || isAnyUploading || isSubmitting
                }
                nothingFound="No KTP documents found"
                required
                withAsterisk
              />
            </div>

            <div style={{ flex: "1 1 45%" }}>
              <Select
                label="NPWP Company"
                placeholder={
                  customerId
                    ? "Select NPWP Company document"
                    : "Select company first"
                }
                value={npwpCompanyId}
                onChange={setNpwpCompanyId}
                data={npwpCompanySelectData}
                searchable
                clearable
                size="md"
                disabled={
                  !customerId ||
                  loadingNpwpCompany ||
                  isAnyUploading ||
                  isSubmitting
                }
                nothingFound="No NPWP Company documents found"
                required
                withAsterisk
              />
            </div>

            <div style={{ flex: "1 1 45%" }}>
              <MultiSelect
                label="NPWP Individu (Optional)"
                placeholder={
                  customerId
                    ? "Select NPWP Individu documents"
                    : "Select company first"
                }
                value={npwpIndividuIds}
                onChange={setNpwpIndividuIds}
                data={npwpIndividuSelectData}
                searchable
                clearable
                size="md"
                disabled={
                  !customerId ||
                  loadingNpwpIndividu ||
                  isAnyUploading ||
                  isSubmitting
                }
                nothingFound="No NPWP Individu documents found"
              />
            </div>
          </Flex>

          <div style={{ borderTop: "2px dashed #1a2332", margin: "10px 0" }} />

          <Text size="md" weight={600}>
            Upload Additional Documents
          </Text>

          <Flex direction="row" gap={20} wrap="wrap">
            <div style={{ flex: "1 1 45%" }}>
              <DocumentUploadWithRecommendation
                label="AHU (Optional)"
                uploadedFile={ahuFile}
                selectedRecommendation={ahuRecommendation}
                isUploading={isUploadingAhu}
                onDrop={handleAhuDrop}
                onSelectRecommendation={handleAhuRecommendationSelect}
                recommendations={ahuRecommendations}
                loadingRecommendations={fetchingAhuRecs}
                disabled={!customerId || isAnyUploading || isSubmitting}
              />
            </div>

            <div style={{ flex: "1 1 45%" }}>
              <DocumentUploadWithRecommendation
                label="NIB"
                uploadedFile={nibFile}
                selectedRecommendation={nibRecommendation}
                isUploading={isUploadingNib}
                onDrop={handleNibDrop}
                onSelectRecommendation={handleNibRecommendationSelect}
                recommendations={nibRecommendations}
                loadingRecommendations={fetchingNibRecs}
                required
                disabled={!customerId || isAnyUploading || isSubmitting}
              />
            </div>

            <div style={{ flex: "1 1 45%" }}>
              <DocumentUploadWithRecommendation
                label="Akta Terbaru"
                uploadedFile={newestAktaFile}
                selectedRecommendation={newestAktaRecommendation}
                isUploading={isUploadingNewestAkta}
                onDrop={handleNewestAktaDrop}
                onSelectRecommendation={handleNewestAktaRecommendationSelect}
                recommendations={newestAktaRecommendations}
                loadingRecommendations={fetchingNewestAktaRecs}
                required
                disabled={!customerId || isAnyUploading || isSubmitting}
              />
            </div>

            <div style={{ flex: "1 1 45%" }}>
              <DocumentUploadWithRecommendation
                label="Akta Pendirian (Optional)"
                uploadedFile={pendirianAktaFile}
                selectedRecommendation={pendirianAktaRecommendation}
                isUploading={isUploadingPendirianAkta}
                onDrop={handlePendirianAktaDrop}
                onSelectRecommendation={handlePendirianAktaRecommendationSelect}
                recommendations={pendirianAktaRecommendations}
                loadingRecommendations={fetchingPendirianAktaRecs}
                disabled={!customerId || isAnyUploading || isSubmitting}
              />
            </div>

            <div style={{ flex: "1 1 100%" }}>
              <MultiDocumentUploadWithRecommendation
                label="Passport (Optional)"
                uploadedFiles={passportFiles}
                selectedRecommendations={passportRecommendations}
                isUploading={isUploadingPassport}
                onDrop={handlePassportDrop}
                onRemove={handleRemovePassport}
                onSelectRecommendations={setPassportRecommendations}
                onRemoveRecommendation={handleRemovePassportRecommendation}
                recommendations={passportRecommendationOptions}
                loadingRecommendations={fetchingPassportRecs}
                disabled={!customerId || isAnyUploading || isSubmitting}
              />
            </div>
          </Flex>
        </Flex>
      </ScrollArea>

      <Flex
        gap={20}
        justify="flex-end"
        p={20}
        style={{
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "white",
        }}
      >
        <Button
          variant="filled"
          size="md"
          uppercase
          onClick={onClose}
          disabled={isAnyUploading || isSubmitting}
          style={{ backgroundColor: "#dc2626" }}
        >
          Cancel
        </Button>
        <Button
          variant="filled"
          size="md"
          uppercase
          onClick={handleSubmit}
          disabled={!isFormValid}
          loading={isSubmitting}
          style={{ backgroundColor: isFormValid ? "#22c55e" : "#ccc" }}
        >
          Conduct KYC
        </Button>
      </Flex>
    </Modal>
  );
};

export default InputDataCompanyModal;
